import { useEffect, useMemo } from "react";
import { options } from "../../data/group-insurance";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { useQueries } from "../services/queries/use-queries";
import { SearchField } from "../../types/type";
import { GetProposalParams } from "../../api/gms/proposal/proposal-api-request";
import { DropdownOption } from "../../features/auth/auth-state";
import { useDispatch, useSelector } from "react-redux";
import { selectAllProposalPagination, setPagination, } from "../../features/pagination/pagination-slice";
import { totalPages, TotalPages } from "../../features/pagination/pagination-inital-state";
import { useGetProposalQuery } from "../../api/gms/proposal/proposal-api";

export const useProposalTable = () => {

    const dispatch = useDispatch();
    const {
        searchType,
        searchValue,
        page,
        defaultTotalPages,
        isTyping,
        isPageSizeChanging,
        isDebounceDone,
    } = useSelector(selectAllProposalPagination);

    const selectedSearchType = searchType ? { id: searchType.id, name: searchType.name } : null;
    const setSelectedSearchType = ( type: { id: number; name: string } | null ) => {
        if (!type) {
            dispatch( setPagination({type: 'allProposal', value: { searchType: null, searchValue: "", page: 1, }}) );
            return;
        }

        const option = options.find((option) => option.id === type.id) ?? null;
        dispatch(setPagination({type: 'allProposal', value: { searchType: option, searchValue: "", page: 1 }}));
    };

    const setSearchValue = (value: string) => {
        dispatch(setPagination({type: 'allProposal', value: { searchValue: value }}));
    };

    const setPage = (value: number) => {
        dispatch(setPagination({type: 'allProposal', value: { page: value }}));
    };

    const params: GetProposalParams = { page, per_page: defaultTotalPages.value };
    if (searchType && searchValue.trim()) {
        params[searchType.value as SearchField] = searchValue.trim();
    }

    const { 
        data: getProposal, 
        error: errorGetProposal, 
        isError: isErrorGetProposal,
        isLoading: isLoadingGetProposal, 
        refetch: refetchGetProposal, 
        isFetching: isFetchingGetProposal
    } = useGetProposalQuery(params);
    const errorMessage = getErrorMessage(errorGetProposal)

    // const {
    //     getProposal,
    //     isLoadingGetProposal,
    //     isErrorGetProposal,
    //     errorGetProposal,
    //     refetchGetProposal,
    //     isFetchingGetProposal,
    // } = useQueries(params);
    // const errorMessage = getErrorMessage(errorGetProposal);

    const pendingProposalCount = useMemo(() => {
        return (getProposal?.data?.filter((proposal) => proposal.status.name === "Pending").length ?? 0);
    }, [getProposal]);

    /** Handle search debounce */
    useEffect(() => {
        dispatch(setPagination({type: 'allProposal', value: { isTyping: true,isDebounceDone: false }}));

        const timer = setTimeout(() => {
            dispatch(setPagination({type: 'allProposal', value: { isDebounceDone: true }}));
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, dispatch]);

    /** Stop typing state after debounce and API request has finished. */
    useEffect(() => {
        if (isDebounceDone && !isFetchingGetProposal) {
            dispatch(setPagination({type: 'allProposal', value: { isTyping: false }}));
        }
    }, [isDebounceDone, isFetchingGetProposal, dispatch]);

    /** Stop page-size loading state after the API request has finished. */
    useEffect(() => {
        if (isPageSizeChanging && !isFetchingGetProposal) {
            dispatch(setPagination({type: 'allProposal', value: { isPageSizeChanging: false }}));
        }
    }, [isPageSizeChanging, isFetchingGetProposal, dispatch]);

    /** Handle page-size change */
    const handleChangePageSize = (value: TotalPages) => {
        dispatch(setPagination({
            type: 'allProposal',
            value: {
                defaultTotalPages: value,
                page: 1,
                isPageSizeChanging: true
            }
        }));
    };

    /** Handle reload */
    const handleReload = () => {
        dispatch(setPagination({type: 'allProposal', value: {page: 1}}));
        refetchGetProposal();
    };

    /** Combined loading state */
    const loading = isTyping || isLoadingGetProposal || isPageSizeChanging;

    /** Normalize search options */
    const normalizedOptions: DropdownOption[] = options.map(
        (option) => ({id: option.id, name: option.name})
    );

    return {
        getProposal,
        pendingProposalCount,
        normalizedOptions,
        errorMessage,
        searchType: selectedSearchType,
        setSearchType: setSelectedSearchType,
        searchValue,
        setSearchValue,
        setPage,
        pagedData: getProposal?.data ?? [],
        loading,
        isErrorGetProposal,
        errorGetProposal,
        handleReload,
        defaultTotalPages,
        setDefaultTotalPages: handleChangePageSize,
        proposalDefaultPageSize: totalPages,
        handleChangePageSize,
    };
};